// services/apiClient.js
import axios from 'axios';
import API_ENDPOINTS from '../config/apiEndpoints';

// Fonction pour récupérer la valeur d'un cookie par son nom (pour CSRF)
function getCookie(name) {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_BASE_URL || '/';

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Intercepteur de requête - Injection automatique du token JWT
apiClient.interceptors.request.use(
    (config) => {
        // Injecter le token d'authentification JWT depuis localStorage ou cookie
        let accessToken = localStorage.getItem('access_token');
        
        // Fallback: lire le token depuis le cookie my-app-auth si pas dans localStorage
        if (!accessToken) {
            accessToken = getCookie('my-app-auth');
        }
        
        if (accessToken) {
            config.headers['Authorization'] = `Bearer ${accessToken}`;
        }

        // Injection du token CSRF pour les requêtes non-sécurisées (POST, PUT, DELETE)
        if (!['GET', 'HEAD', 'OPTIONS'].includes(config.method.toUpperCase())) {
            const csrfToken = getCookie('csrftoken');
            if (csrfToken) {
                config.headers['X-CSRFToken'] = csrfToken;
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Intercepteur de réponse - Gestion des erreurs d'authentification
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const original = error.config;

        // Si erreur 401 et pas déjà en cours de retry
        if (error.response?.status === 401 && !original._retry) {
            original._retry = true;

            const refreshToken = localStorage.getItem('refresh_token');
            
            if (refreshToken) {
                try {
                    console.log("apiClient: Attempting token refresh...");
                    const refreshResponse = await axios.post(
                        `${API_BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
                        { refresh: refreshToken },
                        { withCredentials: true }
                    );

                    if (refreshResponse.data.access) {
                        // Mettre à jour les tokens
                        localStorage.setItem('access_token', refreshResponse.data.access);
                        if (refreshResponse.data.refresh) {
                            localStorage.setItem('refresh_token', refreshResponse.data.refresh);
                        }

                        console.log("apiClient: Token refresh successful. Retrying original request.");
                        
                        // Mettre à jour l'Authorization header et relancer la requête
                        original.headers['Authorization'] = `Bearer ${refreshResponse.data.access}`;
                        return apiClient(original);
                    }

                } catch (refreshError) {
                    console.error("apiClient: Token refresh failed:", refreshError);
                    // Clear tokens and redirect to login
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('refresh_token');
                    localStorage.removeItem('user');
                    
                    // Trigger logout in auth service without causing loops
                    // Don't call authService.logout() here to avoid circular dependency
                    console.log("apiClient: Tokens cleared due to refresh failure.");
                    
                    return Promise.reject(refreshError);
                }
            } else {
                console.log("apiClient: No refresh token available, clearing auth state.");
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('user');
            }
        }

        return Promise.reject(error);
    }
);

export default apiClient;
